import React, { useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DiagnosticsCompanyDetail } from "@/components/consolidation/DiagnosticsCompanyDetail";
import { StatusBadge } from "@/components/consolidation/DiagnosticsStatusBadge";
import { formatDateTimeIT } from "@/foundation/formatting";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border border-suite-neutral-200 bg-white px-3 py-2">
    <div className="text-[11px] text-suite-neutral-500">{label}</div>
    <div className="text-lg font-semibold text-suite-neutral-900" data-testid={testId}>{value}</div>
  </div>
);

const Summary = ({ d }) => {
  const s = d.summary;
  return (
    <div className="space-y-3" data-testid="diagnostics-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Aziende analizzate" value={s.companies} testId="diag-companies-count" />
        <Stat label="Dipendenti analizzati" value={s.employees} testId="diag-employees-count" />
        <Stat label="Dipendenti da verificare" value={s.employees_to_verify} testId="diag-employees-to-verify" />
        <Stat label="Mesi bloccanti" value={s.blocking_months} testId="diag-blocking-months" />
        <Stat label="Consolidamento attivo" value={`${s.consolidation_enabled_companies} aziende`} testId="diag-enabled-companies" />
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-xs">
        <div className="rounded-md border border-suite-neutral-200 bg-white p-3" data-testid="diag-categories">
          <div className="font-semibold mb-1">Mesi per categoria (stato rilevato)</div>
          {Object.entries(d.categories).map(([k, label]) => (
            <div key={k} className="flex justify-between py-0.5"><span><b>{k}</b> · {label}</span>
              <span data-testid={`diag-category-${k}`}>{s.months_by_category[k]}{s.blocking_months_by_category[k] ? ` (${s.blocking_months_by_category[k]} bloccanti)` : ""}</span></div>
          ))}
          <div className="mt-1 text-suite-neutral-500">F conta le anomalie di configurazione, non i mesi.</div>
        </div>
        <div className="rounded-md border border-suite-neutral-200 bg-white p-3" data-testid="diag-severity">
          <div className="font-semibold mb-1">Anomalie per gravità</div>
          <div>Bloccanti: {s.issues_by_severity.blocking} · Attenzione: {s.issues_by_severity.warning} · Informative: {s.issues_by_severity.info}</div>
          <div className="font-semibold mt-2 mb-1">Aziende per stato</div>
          <div>Pronte: {s.companies_by_status.ready} · Da verificare: {s.companies_by_status.to_verify} · Non configurate: {s.companies_by_status.not_configured} · Baseline approvata: {s.companies_by_status.baseline_approved}</div>
        </div>
      </div>
    </div>
  );
};

const CompaniesTable = ({ items, selected, onSelect }) => (
  <div className="overflow-x-auto rounded-lg border border-suite-neutral-200 bg-white">
    <table className="w-full text-xs" data-testid="diag-companies-table">
      <thead className="bg-suite-neutral-50 text-suite-neutral-600">
        <tr>{["Azienda", "Stato", "Dipendenti", "Con mesi chiusi", "Da verificare", "Mesi bloccanti", "Motivo", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.company_id} className={`border-t border-suite-neutral-100 ${selected === c.company_id ? "bg-amber-50" : ""}`} data-testid={`diag-company-row-${c.company_id}`}>
            <td className="px-3 py-2 font-medium">{c.name}</td>
            <td className="px-3 py-2"><StatusBadge status={c.status} label={c.status_label} testId={`diag-company-status-${c.company_id}`} /></td>
            <td className="px-3 py-2">{c.employees}</td><td className="px-3 py-2">{c.employees_with_closures}</td>
            <td className="px-3 py-2">{c.employees_to_verify}</td><td className="px-3 py-2">{c.blocking_months}</td>
            <td className="px-3 py-2 text-suite-neutral-600">{c.reason}</td>
            <td className="px-3 py-2"><Button size="sm" variant="outline" onClick={() => onSelect(c.company_id)} disabled={!c.employees} data-testid={`diag-company-detail-btn-${c.company_id}`}>Dettaglio</Button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Notes = ({ d }) => (
  <div className="grid md:grid-cols-2 gap-3 text-xs">
    <div className="rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="diag-recommendations">
      <div className="font-semibold mb-1">Raccomandazioni tecniche (separate dai dati rilevati)</div>
      <ul className="list-disc pl-4 space-y-0.5">{d.recommendations.map((r) => <li key={r}>{r}</li>)}</ul>
    </div>
    <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 p-3" data-testid="diag-not-verified">
      <div className="font-semibold mb-1">Elementi non verificati</div>
      <ul className="list-disc pl-4 space-y-0.5">{d.not_verified.map((r) => <li key={r}>{r}</li>)}</ul>
    </div>
  </div>
);

export function BaselineDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const run = async () => {
    setLoading(true);
    try { setData((await api.get("/counters-consolidation/diagnostics")).data); setSelected(""); } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  };
  return (
    <section className="space-y-3 border-t border-suite-neutral-200 pt-5" data-testid="baseline-diagnostics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-suite-neutral-900">Diagnostica pre-baseline · sola lettura</h2>
          <p className="text-xs text-suite-neutral-600">Mesi che impediscono generazione o approvazione delle baseline. Nessun dato viene modificato e nessuna azione viene eseguita.</p>
        </div>
        <Button onClick={run} disabled={loading} data-testid="run-diagnostics-btn"><Search size={14} className="mr-1" />{loading ? "Analisi in corso…" : data ? "Ripeti diagnostica" : "Esegui diagnostica"}</Button>
      </div>
      {data && (
        <>
          <div className="text-[11px] text-suite-neutral-500" data-testid="diag-generated-at">Analisi del {formatDateTimeIT(data.generated_at)} · periodo fino a {data.scope.until}</div>
          <Summary d={data} />
          <CompaniesTable items={data.companies} selected={selected} onSelect={setSelected} />
          {selected && <DiagnosticsCompanyDetail companyId={selected} />}
          <Notes d={data} />
        </>
      )}
    </section>
  );
}
